import { Injectable } from '@angular/core';
import { AssignmentSectionQuestionQuery, AssignmentSectionQuestionService } from '@examdojo/assignment';
import {
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityService,
  ChallengeReviewData,
} from '@examdojo/challenge';
import { mapToVoid } from '@examdojo/rxjs';
import { QuestionAttemptEntityService } from '@examdojo/question';
import { Observable, of, switchMap, tap } from 'rxjs';

@Injectable()
export class ChallengeReviewDataService {
  constructor(
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly assignmentSectionQuestionService: AssignmentSectionQuestionService,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
  ) {}

  fetchReviewData(challengeActivityId: string): Observable<void> {
    this.questionAttemptEntityService.resetStore();

    return this.challengeActivityService.getReviewData(challengeActivityId).pipe(
      tap((reviewData) => this.storeReviewData(reviewData)),
      switchMap((reviewData) => this.fetchQuestionAttemptDetails(reviewData)),
      mapToVoid(),
    );
  }

  setFirstQuestionAsActive(challengeActivityId: string): void {
    const questionAttempts = this.challengeActivityQuestionAttemptQuery.getByChallengeActivityId(challengeActivityId);
    const assignmentSectionQuestions = this.assignmentSectionQuestionQuery
      .getAllEntities()
      .toSorted((a, b) => a.position - b.position);

    const firstQuestion = assignmentSectionQuestions.find((question) =>
      questionAttempts.some((attempt) => attempt.question_id === question.question_id),
    );

    if (!firstQuestion) {
      return;
    }

    const firstAttempt = questionAttempts.find((attempt) => attempt.question_id === firstQuestion.question_id);

    if (!firstAttempt?.question_attempt_id) {
      return;
    }

    this.assignmentSectionQuestionService.setActiveQuestion(
      firstQuestion.assignment_section_id,
      firstQuestion.question_id,
    );
    this.questionAttemptEntityService.setActive(firstAttempt.question_attempt_id);
    this.challengeActivityQuestionAttemptService.setActive(challengeActivityId, firstQuestion.question_id);
  }

  private storeReviewData(reviewData: ChallengeReviewData): void {
    this.assignmentSectionQuestionService.upsertManyHttpModels(reviewData.assignment_section_questions);
    this.challengeActivityQuestionAttemptService.upsertManyHttpModels(reviewData.challenge_activity_question_attempts);
  }

  private fetchQuestionAttemptDetails(reviewData: ChallengeReviewData): Observable<void> {
    const questionAttemptIds = reviewData.challenge_activity_question_attempts
      .map((attempt) => attempt.question_attempt_id)
      .filter((id): id is string => id !== null);

    if (questionAttemptIds.length === 0) {
      return of(undefined);
    }

    return this.challengeActivityService.fetchQuestionAttemptDetailsBulk(questionAttemptIds);
  }
}
